import { motion } from "motion/react";
import { ArrowLeft, ExternalLink, Github, ArrowUpRight } from "lucide-react";
import type { Project } from "@/data/projects";
import { projects } from "@/data/projects";

interface ProjectDetailProps {
  mode: "single" | "list";
  project?: Project | null;
  onClose: () => void;
}

export function ProjectDetail({ mode, project, onClose }: ProjectDetailProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: "100%" }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: "100%" }}
      transition={{ duration: 0.6, ease: [0.32, 0.72, 0, 1] }}
      className="fixed inset-0 z-60 bg-background overflow-y-auto"
    >
      <div className="min-h-screen">
        {/* Header / Nav */}
        <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border">
          <div className="container mx-auto px-6 h-20 flex items-center justify-between">
            <button
              onClick={onClose}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors group"
            >
              <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
              <span className="font-medium">Back to Portfolio</span>
            </button>
            <div className="font-mono text-sm uppercase tracking-widest text-muted-foreground">
              {mode === "single" ? "Project Details" : "All Experiments"}
            </div>
          </div>
        </div>

        {mode === "single" && project ? (
          // === SINGLE PROJECT VIEW ===
          <SingleProjectView project={project} />
        ) : (
          // === FULL LIST VIEW ===
          <FullListView />
        )}
      </div>
    </motion.div>
  );
}

function SingleProjectView({ project }: { project: Project }) {
  return (
    <div className="container mx-auto px-6 py-20 md:py-32">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="mb-12"
      >
        <div className="flex gap-2 mb-4">
          {project.badges?.map((badge) => (
            <span
              key={badge}
              className="text-blue-600 font-mono text-sm tracking-widest uppercase block"
            >
              {badge} •
            </span>
          ))}
        </div>

        <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-linear-to-br from-foreground to-muted-foreground bg-clip-text text-transparent leading-[1.1]">
          {project.title}
        </h1>
        <p className="text-2xl md:text-3xl text-muted-foreground max-w-3xl leading-relaxed">
          {project.description}
        </p>
      </motion.div>

      {/* Content Grid */}
      <div className="grid md:grid-cols-12 gap-12 md:gap-24 mb-24">
        {/* Large Hero Image */}
        <motion.img
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4 }}
          src={project.image}
          alt={project.title}
          className="w-full aspect-video bg-muted rounded-3xl object-cover object-center grayscale hover:grayscale-0 transition-all duration-700 md:col-span-8"
        />
        {/* Sidebar / Metadata */}
        <div className="md:col-span-4 space-y-12">
          {project.techStack && (
            <div>
              <h3 className="text-sm font-mono uppercase tracking-widest text-muted-foreground mb-4">
                Tech Stack
              </h3>
              <div className="flex flex-wrap gap-2">
                {project.techStack.map((tech) => (
                  <span
                    key={tech}
                    className="px-3 py-1 bg-muted text-sm rounded-full"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          )}

          {project.links && (
            <div>
              <h3 className="text-sm font-mono uppercase tracking-widest text-muted-foreground mb-4">
                Links
              </h3>
              <div className="space-y-3">
                {project.links.map((link) => (
                  <a
                    key={link.label}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-foreground hover:text-blue-600 transition-colors"
                  >
                    <ExternalLink className="w-5 h-5" />
                    <span>{link.label}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content (Placeholder for now, using longDesc) */}
      <div className="space-y-20 max-w-4xl">
        <section>
          <h2 className="text-3xl font-bold mb-6">About</h2>
          <p className="text-lg text-muted-foreground leading-relaxed whitespace-pre-wrap">
            {project.longDescription || project.description}
          </p>
        </section>
      </div>
    </div>
  );
}

function FullListView() {
  return (
    <div className="container mx-auto px-6 py-20 max-w-5xl">
      <div className="space-y-12">
        {/* Custom minimalist table-like list */}
        <div className="grid grid-cols-1 gap-8">
          {projects.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="group border-b border-border/50 pb-8 last:border-0"
            >
              <div className="grid md:grid-cols-12 gap-4 items-start">
                {/* Title & Tech */}
                <div className="md:col-span-4">
                  <h3 className="text-2xl font-bold text-foreground mb-2 group-hover:text-blue-600 transition-colors">
                    {p.title}
                  </h3>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {p.techStack?.slice(0, 3).map((t) => (
                      <span
                        key={t}
                        className="text-xs font-mono text-muted-foreground bg-muted/50 px-2 py-1 rounded"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Description */}
                <div className="md:col-span-6">
                  <p className="text-muted-foreground leading-relaxed">
                    {p.description}
                  </p>
                </div>

                {/* Link */}
                <div className="md:col-span-2 flex justify-end">
                  <a
                    href="#"
                    className="p-2 rounded-full border border-border hover:bg-foreground hover:text-background transition-colors"
                  >
                    <ArrowUpRight className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
